import React, { useCallback, useContext, useEffect, useState } from 'react';
import { usePack, usePayment } from '#src/@billing/use-billing.js';
import Dialog from '#lib/components/dialog.jsx';
import Button from '#lib/components/button.jsx';
import { Icon } from '@iconify-icon/react';
import PackCard from '#components/pack-card.jsx';
import { addClasses, cn } from '#lib/helpers/class.js';
import { Elements } from '@stripe/react-stripe-js';
import StripeCheckout from '#components/stripe-checkout.jsx';
import { findValidItem, PackagesIDs } from './billing-helpers.js';
import toast from 'react-hot-toast';
import Input from '#lib/components/input.jsx';
import useCoupon from './use-coupon.js';
import { UserContext } from '#src/index.jsx';
import { useNavigate } from 'react-router-dom';

export default function PackagesModal(props) {
    const { isOpen, onClose, stripePromise, addUsers, purchases } = props;
    const navigate = useNavigate();
    const { data } = usePack();
    const [packages, setPackages] = useState([]);
    const [selectedPack, setselectedPack] = useState(null);
    const [couponId, setCouponId] = useState(null);
    const [coupon, setCoupon] = useState(null);
    const [discounts, setDiscounts] = useState({});
    const [showCoupon, setShowCoupon] = useState(false);
    const [activePack, setActivePack] = useState(findValidItem(purchases, packages));
    const { verifyCoupon, createUserCoupon } = useCoupon({
        onSuccess: (res) => {
            if (!res.valid) return toast.error(_t.billing.couponNotOk());
            setCouponId(res.couponId);
            if (res.discounts.hasOwnProperty(PackagesIDs.ALL)) {
                const discount = res.discounts[PackagesIDs.ALL];
                setDiscounts({
                    [PackagesIDs.STANDARD]: discount,
                    [PackagesIDs.PREMIUM]: discount,
                    [PackagesIDs.UNLIMITED]: discount
                });
            } else setDiscounts(res.discounts);
            toast.success(_t.billing.couponOk());
        }
    });

    const onClick = () => {
        verifyCoupon.mutate(coupon);
        setCoupon('');
    };

    const checkIfDiscountable = useCallback((id) => discounts.hasOwnProperty(id), [discounts]);

    const payment = usePayment({
        onSuccess: (res, model) => {
            if (res?.ok) {
                const pack = packages.find((el) => el.id === model.packId);
                const isDiscountable = checkIfDiscountable(model.packId);
                setselectedPack({ ...pack, clientSecret: res.clientSecret });
                if (couponId && isDiscountable && discounts[model.packId] === 100) {
                    createUserCoupon.mutate(couponId);
                    close();
                    navigate('../payment?redirect_status=succeeded');
                }
            }
        }
    });

    useEffect(() => {
        setPackages(
            addUsers
                ? data
                      ?.filter((pack) => pack.id !== PackagesIDs.WELCOME)
                      ?.sort((a, b) => {
                          if (a.id === 'USER') return 1;
                          return a.price - b.price;
                      })
                      ?.map((el) => {
                          return {
                              id: el.id,
                              price: el.id === 'USER' ? el.price : el.price * 12,
                              description: el.description,
                              priority: el.priority
                          };
                      })
                : data
                      ?.filter((pack) => pack.id !== 'USER' && pack.id !== 'WELCOME')
                      ?.sort((a, b) => a.price - b.price)
                      ?.map((el) => {
                          return {
                              id: el.id,
                              price: el.price * 12,
                              description: el.description,
                              priority: el.priority
                          };
                      })
        );
    }, [data]);

    useEffect(() => setActivePack(findValidItem(purchases, packages)), [packages, purchases]);

    function close() {
        setselectedPack(null);
        setDiscounts({});
        setCouponId(null);
        onClose();
    }

    const { getLanguage } = useContext(UserContext);
    const language = getLanguage();

    const userPack = packages?.find((pack) => pack.id === 'USER');

    if (packages) {
        return (
            <Dialog isOpen={isOpen} onClose={close}>
                <Dialog.Panel className="max-h-[95%] max-w-[850px] overflow-auto">
                    <div className="pr-4">
                        <Dialog.CloseButton />
                        <h1 className="font-bold text-lg">{_t.billing.buy()}</h1>
                        <p
                            lang="en"
                            className={cn(
                                'max-w-min min-w-full pr-2 mt-1 mb-2',
                                !selectedPack && 'text-sm text-neutral-600'
                            )}
                        >
                            {selectedPack ? `You're buying ${selectedPack.description}` : _t.billing.buyDesc()}
                        </p>

                        {!selectedPack && (
                            <CouponSection
                                discounts={discounts}
                                couponId={couponId}
                                setCouponId={setCouponId}
                                coupon={coupon}
                                setCoupon={setCoupon}
                                setDiscounts={setDiscounts}
                                showCoupon={showCoupon}
                                setShowCoupon={setShowCoupon}
                                onClick={onClick}
                            />
                        )}
                    </div>

                    {selectedPack ? (
                        <StripeCheckoutSection
                            stripePromise={stripePromise}
                            selectedPack={selectedPack}
                            setselectedPack={setselectedPack}
                            discount={discounts[selectedPack.id]}
                            couponId={couponId}
                            createUserCoupon={createUserCoupon}
                            checkIfDiscountable={checkIfDiscountable}
                        />
                    ) : (
                        <Packages
                            discounts={discounts}
                            payment={payment}
                            packages={packages}
                            packId={selectedPack?.id}
                            activePack={activePack}
                            userPack={userPack}
                            checkIfDiscountable={checkIfDiscountable}
                            language={language}
                        />
                    )}
                </Dialog.Panel>
            </Dialog>
        );
    }
}

function CouponSection(props) {
    const { discounts, couponId, setCouponId, coupon, setCoupon, setDiscounts, onClick } = props;
    return (
        <div className="flex gap-2">
            <div className="flex flex-col gap-4 transition-all duration-500 opacity-100">
                <div className="flex gap-2 items-center">
                    <Input.Full
                        value={coupon}
                        placeHolder="Coupon"
                        onChange={(e) => setCoupon(e.target.value)}
                        readOnly={couponId}
                        className={addClasses('w-[300px]')}
                        inputClassName={addClasses('w-full')}
                    />
                    <Button
                        look="primary"
                        disabled={couponId || !coupon}
                        onClick={onClick}
                        className={addClasses('w-24')}
                    >
                        {_t.billing.useCoupon()}
                    </Button>
                </div>

                {couponId && (
                    <div className="h-[45px] flex items-center justify-between w-full rounded-lg shadow-md px-3 py-3 border border-neutral-100">
                        <div className="flex gap-2">
                            <p className="font-semibold">{`${couponId}: `}</p>
                            <p>
                                {Object.entries(discounts)
                                    .sort(([packA, discountA], [packB, discountB]) => discountA - discountB)
                                    .map(([pack, discount]) => _t.billing.discountText(pack, discount))
                                    .join(', ')}
                            </p>
                        </div>
                        <p
                            onClick={() => {
                                setDiscounts({});
                                setCouponId(null);
                            }}
                            className="cursor-pointer mt-1 ml-1"
                        >
                            <Icon icon="iconoir:xmark" width="1.3rem" height="1.3rem" />
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}

function StripeCheckoutSection(props) {
    const { stripePromise, selectedPack, setselectedPack, discount, couponId, createUserCoupon, checkIfDiscountable } =
        props;
    return (
        <div className="relative grid gap-2 mt-2 px-4 py-4">
            <div>
                <Elements stripe={stripePromise} options={{ clientSecret: selectedPack.clientSecret }}>
                    <StripeCheckout
                        pack={selectedPack}
                        coupon={checkIfDiscountable(selectedPack.id) ? couponId : null}
                        discount={discount}
                        createCoupon={createUserCoupon}
                    />
                </Elements>
            </div>

            <button
                onClick={() => {
                    setselectedPack(null);
                }}
                className="absolute left-4 bottom-5 inline-block bg-slate-200 rounded-full px-3 py-1 text-sm font-semibold text-emerald-600 hover:bg-emerald-600 hover:text-white"
            >
                {_t.billing.changePack()}
            </button>
        </div>
    );
}

function Packages(props) {
    const { discounts, payment, packages, activePack, userPack, checkIfDiscountable, language } = props;

    const handleBuyPack = async (packId) => {
        payment.mutate({
            packId,
            discount: checkIfDiscountable(packId) ? discounts[packId] : 0,
            language
        });
    };
    return (
        <div>
            <div className="grid grid-cols-1 md:grid-cols-3 items-stretch justify-start w-full overflow-x-auto gap-2 py-4">
                {packages.map((pack, i) => {
                    if (pack.id === 'USER') return;
                    return (
                        <div className="col-span-1 h-[331px]" key={i}>
                            <PackCard
                                key={i}
                                pack={pack}
                                onBuy={async (packId) => {
                                    payment.mutate({
                                        packId,
                                        discount: checkIfDiscountable(packId) ? discounts[packId] : 0,
                                        language
                                    });
                                }}
                                isLoading={payment.isLoading && payment.variables?.packId === pack.id}
                                discount={discounts[pack.id]}
                                activePack={activePack}
                            />
                        </div>
                    );
                })}
            </div>
            {userPack && (
                <div className="w-full py-3 items-start border-t">
                    <span className="font-bold">{_t.billing.additionalPackages()}</span>
                    <p className="pr-2 mt-1 mb-4 text-sm text-neutral-600 text-justify">
                        {_t.billing.additionalPackagesDesc()}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                        <div className="col-span-1">
                            <PackCard
                                pack={userPack}
                                onBuy={(packId) => handleBuyPack(packId)}
                                isLoading={payment.isLoading && payment.variables?.packId === userPack.id}
                                discount={discounts?.[userPack?.id]}
                                activePack={activePack}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
